import { Autocomplete, Box, Chip, TextField, Tooltip } from '@mui/material';

export type PillOption = { value: string; label: string; disabled?: boolean; disabledTooltip?: string };

type PillSelectProps = {
  label: string;
  value: string;
  options: PillOption[];
  onChange: (value: string) => void;
  /** Tooltip of the selected value. */
  tooltip?: string;
  onOpen?: () => void;
};

const BORDER = 'rgba(145, 158, 171, 0.2)';
const LABEL = '#333C47';

/**
 * Toolbar selector built like the header filters (header.tsx, platforms pill): a MUI Autocomplete whose value is
 * shown as a chip, the options list taking the field's width. One value at a time: picking an option replaces the
 * current one, and the chip cannot be removed. Sized as the maquette's selectors (Figma 7860:19591): bordered box
 * with a 3px padding, grey pill of 23px (13px label), 18px chevron, as wide as its label.
 */
export default function PillSelect({ label, value, options, onChange, tooltip, onOpen }: PillSelectProps) {
  const byValue = new Map(options.map((option) => [option.value, option]));
  const labelOf = (v: string) => byValue.get(v)?.label ?? v;
  return (
    <Tooltip title={tooltip ?? ''} placement="top">
      <Box sx={{ flexShrink: 0 }}>
        <Autocomplete
          sx={{
            whiteSpace: 'nowrap',
            '& .MuiAutocomplete-inputRoot': {
              flexWrap: 'nowrap',
              // 3 + pill 23 + 3, the border drawn inside like the Figma stroke (outline fieldset).
              height: '29px',
              padding: '3px !important',
              borderRadius: '8px',
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
            '& .MuiChip-root': {
              height: 23,
              // 8px to the chevron; a flex gap would also count the (empty) input.
              margin: '0 8px 0 0',
              borderRadius: '6px',
              backgroundColor: BORDER,
              color: LABEL,
              '&:hover': { backgroundColor: 'rgba(145, 158, 171, 0.32)' },
            },
            '& .MuiChip-label': { px: '8px', fontSize: 13, fontWeight: 500, lineHeight: '23px' },
            // No typing area: the box is as wide as its chip + chevron, like the maquette.
            '& .MuiAutocomplete-input': { width: '0 !important', minWidth: '0 !important', padding: '0 !important' },
            '& .MuiAutocomplete-endAdornment': { position: 'static', transform: 'none', display: 'flex' },
            '& .MuiAutocomplete-popupIndicator': { p: '2px', mr: 0, color: LABEL, '& svg': { width: 18, height: 18 } },
          }}
          // The list keeps the field's width, but a longer option ("Last 12 weeks" under "Last week") stays readable.
          slotProps={{ paper: { sx: { width: 'max-content', minWidth: '100%' } } }}
          multiple
          limitTags={1}
          disableClearable
          size="small"
          onOpen={onOpen}
          options={options.map((option) => option.value)}
          getOptionLabel={labelOf}
          getOptionDisabled={(option) => !!byValue.get(option)?.disabled}
          value={[value]}
          onChange={(_event, selected) => {
            const next = selected.filter((option) => option !== value).pop();
            if (next) onChange(next);
          }}
          renderInput={(params) => (
            <TextField {...params} inputProps={{ ...params.inputProps, 'aria-label': label }} />
          )}
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={labelOf(option)}
                size="small"
                variant="soft"
                onDelete={undefined}
              />
            ))
          }
          renderOption={(props, option) => {
            const item = byValue.get(option);
            return (
              <li {...props} key={option}>
                {item?.disabled && item.disabledTooltip ? (
                  // The <li> stays a direct child of the list; the label takes the pointer back for its tooltip.
                  <Tooltip title={item.disabledTooltip} placement="left" describeChild>
                    <Box component="span" sx={{ pointerEvents: 'auto', width: '100%' }}>
                      {labelOf(option)}
                    </Box>
                  </Tooltip>
                ) : (
                  labelOf(option)
                )}
              </li>
            );
          }}
        />
      </Box>
    </Tooltip>
  );
}
